import type { RowData, Table } from '@tanstack/react-table';
import { useEffect } from 'react';

import type { TDataTableFeatures } from '../features/types/features.types.js';

/**
 * Updates the page index to the last non-empty page when the current page is empty.
 * Necessary for paginated tables with sub-rows.
 * @internal
 */
export function usePaginationEmptyPageCorrection<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
): void {
  const currentPageRowCount = table.getPaginatedRowModel().rows.length;
  const { enablePagination, controlledPageIndex } = table.options;
  const setPageIndex = table.setPageIndex;
  const getCoreRowModel = table.getCoreRowModel;
  const getPagination = table.atoms.pagination?.get;

  useEffect(() => {
    if (enablePagination === false || controlledPageIndex === true) {
      return;
    }

    const { pageIndex, pageSize } = getPagination?.() ?? {};

    // If there are no rows to display on the current page, navigate to the last non-empty page
    if (pageIndex > 0 && currentPageRowCount === 0) {
      const totalRowCount = getCoreRowModel().rows.length;
      const firstNonEmptyPrevPage = Math.ceil(totalRowCount / pageSize) - 1;
      setPageIndex(firstNonEmptyPrevPage, 'internal');
    }
  }, [
    controlledPageIndex,
    currentPageRowCount,
    enablePagination,
    getCoreRowModel,
    getPagination,
    setPageIndex,
  ]);
}
